<template>
    <div>
        <div>
            <quill-editor
                ref="editor"
                v-model="localContent"
                :options="editorOptions"
                class="template-editor"
                @change="onEditorChange"
            />
        </div>

        <div class="flex flex-col w-full py-2" v-if="tags.length">
            <div class="flex overflow-x-auto pb-2 no-scrollbar flex-wrap">
                <a
                    href="#"
                    class="flex-shrink-0 mb-3"
                    v-for="tag in tags"
                    :key="tag.value"
                    @click.prevent="addTag(tag.value)"
                >
                    <span class="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-5 py-1 rounded-sm dark:bg-blue-900 dark:text-blue-300 cursor-pointer inline-flex items-center badge-tag">
                        <svg class="w-4 h-4 me-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        {{ tag.name }}
                    </span>
                </a>
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
                <span class="inline-flex">
                    <svg class="w-4 h-4 me-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {{ infoText || 'Click on tags to insert them into your template' }}
                </span>
                <br />
                {{ subInfoText || 'Tags will be replaced with actual values when the template is used' }}
            </div>
        </div>
    </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.snow.css'

export default {
    name: 'TemplateEditor',
    components: {
        quillEditor
    },
    props: {
        content: {
            type: String,
            required: true,
        },
        placeholder: {
            type: String,
            default: 'Type your content here...',
        },
        toolbarOptions: {
            type: Array,
            default: () => [],
        },
        tags: {
            type: Array,
            default: () => [],
            // Example: [{ name: 'First Name', value: 'first_name' }, { name: 'Last Name', value: 'last_name' }]
        },
        infoText: {
            type: String,
            default: ''
        },
        subInfoText: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            localContent: this.content
        }
    },
    computed: {
        editorOptions() {
            return {
                placeholder: this.placeholder,
                modules: {
                    toolbar: this.toolbarOptions.length > 0 ? this.toolbarOptions : false
                }
            }
        }
    },
    watch: {
        content(newVal) {
            if (newVal !== this.localContent) {
                this.localContent = newVal
            }
        }
    },
    methods: {
        onEditorChange({ html, text }) {
            this.$emit('update:content', html)
            this.$emit('update:text', text)
        },
        addTag(tag) {
            if (tag) {
                const quill = this.$refs.editor.quill
                const range = quill.getSelection()

                if (range) {
                    quill.insertText(range.index, `{{${tag}}}`, 'user')
                    quill.setSelection(range.index + tag.length + 4)
                } else {
                    const length = quill.getLength()
                    quill.insertText(length - 1, `{{${tag}}}`, 'user')
                    quill.setSelection(length + tag.length + 3)
                }
            }
        }
    }
}
</script>

<style scoped>
.template-editor {
    min-height: 150px;
}

.template-editor >>> .ql-container {
    min-height: 150px;
}

.template-editor >>> .ql-editor {
    min-height: 150px;
}

.flex {
    display: flex;
}

.flex-col {
    flex-direction: column;
}

.flex-wrap {
    flex-wrap: wrap;
}

.w-full {
    width: 100%;
}

.py-2 {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
}

.pb-2 {
    padding-bottom: 0.5rem;
}

.overflow-x-auto {
    overflow-x: auto;
}

.flex-shrink-0 {
    flex-shrink: 0;
}

.mb-3 {
    margin-bottom: 0.75rem;
}

.bg-blue-100 {
    background-color: #dbeafe;
}

.text-blue-800 {
    color: #1e40af;
}

.text-xs {
    font-size: 0.75rem;
    line-height: 1rem;
}

.font-medium {
    font-weight: 500;
}

.me-2 {
    margin-right: 0.5rem;
}

.me-1\.5 {
    margin-right: 0.375rem;
}

.px-5 {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
}

.py-1 {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
}

.rounded-sm {
    border-radius: 0.125rem;
}

.cursor-pointer {
    cursor: pointer;
}

.inline-flex {
    display: inline-flex;
}

.items-center {
    align-items: center;
}

.text-gray-500 {
    color: #6b7280;
}

.w-4 {
    width: 1rem;
}

.h-4 {
    height: 1rem;
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.badge-tag {
    border: 1px solid #1e3a8a;
    text-transform: uppercase;
}

/* Dark mode styles */
.dark .bg-blue-100 {
    background-color: #1e3a8a;
}

.dark .text-blue-800 {
    color: #93c5fd;
}

.dark .text-gray-500 {
    color: #9ca3af;
}

.dark .text-gray-400 {
    color: #9ca3af;
}

/* Quill dark mode fixes */
.dark .ql-snow.ql-toolbar button,
.dark .ql-snow .ql-toolbar button {
    filter: invert(1);
}

.dark .ql-snow.ql-toolbar .ql-picker-label,
.dark .ql-snow .ql-toolbar .ql-picker-label {
    color: white;
}

.dark .ql-editor.ql-blank::before {
    color: rgba(255, 255, 255, 0.6);
}

.dark .ql-snow.ql-container {
    border-color: #374151;
}

.dark .ql-snow.ql-toolbar {
    border-color: #374151;
}
</style>